import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CalculatorSettings } from '../types';

interface Props {
  settings: CalculatorSettings;
  onChange: (next: CalculatorSettings) => void;
}

const CalculatorInputs: React.FC<Props> = ({ settings, onChange }) => {
  const { t } = useTranslation('admin');

  const [costDraft, setCostDraft] = useState(settings.costPerCall.toFixed(2));
  const costInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCostDraft(settings.costPerCall.toFixed(2));
  }, [settings.costPerCall]);

  const commitCost = useCallback(
    (raw: string) => {
      const parsed = parseFloat(raw);
      if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 10 && parsed !== settings.costPerCall) {
        onChange({ costPerCall: parsed });
      } else {
        setCostDraft(settings.costPerCall.toFixed(2));
      }
    },
    [settings, onChange],
  );

  // The native `change` event fires immediately when a number input is stepped via the spinner
  // buttons or the Up/Down arrow keys, but only on blur for typed input. React's onChange maps to
  // the `input` event (every keystroke), so we listen to the native `change` event here to commit
  // stepper changes right away while still committing typed values on blur.
  useEffect(() => {
    const costEl = costInputRef.current;
    const onCostChange = (e: Event) => commitCost((e.target as HTMLInputElement).value);
    costEl?.addEventListener('change', onCostChange);
    return () => {
      costEl?.removeEventListener('change', onCostChange);
    };
  }, [commitCost]);

  return (
    <div className="calculator-inputs">
      <div className="calculator-input-field">
        <label htmlFor="cost-per-call-input">
          {t('roiInsights.calculator.costPerCall')}
          <span className="required-asterisk"> *</span>
        </label>
        <input
          ref={costInputRef}
          id="cost-per-call-input"
          className="cost-per-call-input"
          type="number"
          step="0.01"
          min="0"
          max="10"
          value={costDraft}
          onChange={e => setCostDraft(e.target.value)}
          onBlur={e => commitCost(e.target.value)}
        />
      </div>
    </div>
  );
};

export default CalculatorInputs;
